import React, { useState } from "react";
import { Card, Stack } from "react-bootstrap";
import { HousingCandidate, bedsOfRoom, pluralizeCount, sortBedsByName } from "../../helpers/housingAssignment";
import BedAssignmentRow from "./BedAssignmentRow";
import HousingStatus from "../ui/HousingStatus";
import RoomBedsPanel, { ManageBedsButton } from "../ui/RoomBedsPanel";
import useRoomAssignmentActions from "./useRoomAssignmentActions";

interface RoomAssignmentRowState {
  room: any;
  facilities: any[];
  rooms: any[];
  beds: any[];
  housingCandidates: HousingCandidate[];
  housingByCaseId: Map<number, HousingCandidate>;
  possibleOccupants: any[];
  onAssigned: any;
  onPickerOpenChange: any;
  setErrMessage: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const RoomAssignmentRow = ({
  room,
  facilities,
  rooms,
  beds,
  housingCandidates,
  housingByCaseId,
  possibleOccupants,
  onAssigned,
  onPickerOpenChange,
  setErrMessage,
  setShowToast,
  setToastText,
  setToastBody,
}: RoomAssignmentRowState) => {
  const [bedsOpen, setBedsOpen] = useState(false);
  const [bedSearch, setBedSearch] = useState("");
  const roomBeds = sortBedsByName(bedsOfRoom(beds, room.id));

  const {
    mode,
    capacity,
    statusDotClass,
    statusLabel,
    fillPercent,
    fillBarClass,
    capacityNote,
    confirmingEnd,
    canAssignFamily,
    pickerOpen,
    actions,
    pickerPanel,
  } = useRoomAssignmentActions({
      room,
      facilities,
      rooms,
      beds,
      housingCandidates,
      housingByCaseId,
      onAssigned,
      onPickerOpenChange,
      setErrMessage,
      setShowToast,
      setToastText,
      setToastBody,
    });

  // a family room has no individual beds, and picking a case takes the row over
  const showManageBeds = mode !== "family" && !pickerOpen;

  // who the case assigned to this room actually is - the status line only names the case
  const occupantPeople =
    mode === "family" ? possibleOccupants.filter((p: any) => p.case_id === room.family_case_id && p.is_residential === 1) : [];

  return (
    <Card className="mb-0 position-relative overflow-hidden">
      <Card.Body className="px-3 py-3">
        <Stack direction="horizontal" gap={3} className="flex-wrap">
          <div className="flex-grow-1 order-0">
            <div className="small fw-semibold">{room.name}</div>
            <div className="text-muted small">
              {pluralizeCount(capacity, "bed")}
              {capacityNote && <> · {capacityNote}</>}
            </div>
          </div>
          {!confirmingEnd && <HousingStatus dotClass={statusDotClass} label={statusLabel} people={occupantPeople} />}
          <Stack direction="horizontal" gap={2} className="flex-wrap align-items-center order-1 order-xl-3">
            {!bedsOpen && actions}
            {showManageBeds && canAssignFamily && !bedsOpen && <div className="vr" />}
            {showManageBeds && (
              <ManageBedsButton
                open={bedsOpen}
                bedCount={capacity}
                onToggle={() => {
                  setBedSearch("");
                  setBedsOpen((v) => !v);
                }}
              />
            )}
          </Stack>
        </Stack>

        {pickerPanel}

        {showManageBeds && bedsOpen && (
          <RoomBedsPanel
            beds={roomBeds}
            search={bedSearch}
            setSearch={setBedSearch}
            renderBed={(bed: any) => (
              <BedAssignmentRow
                bed={bed}
                beds={beds}
                rooms={rooms}
                facilities={facilities}
                possibleOccupants={possibleOccupants}
                housingByCaseId={housingByCaseId}
                onAssigned={onAssigned}
                onPickerOpenChange={onPickerOpenChange}
                setErrMessage={setErrMessage}
                setShowToast={setShowToast}
                setToastText={setToastText}
                setToastBody={setToastBody}
              />
            )}
          />
        )}
      </Card.Body>
      {mode === "individual" && (
        <div className="room-fill-bar">
          <div className={`room-fill-bar-fill ${fillBarClass}`} style={{ width: `${fillPercent}%` }} />
        </div>
      )}
    </Card>
  );
};

export default RoomAssignmentRow;
